import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { IncomeStructureByKvedGeneralInfoSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/IncomeStructureByKvedGeneralInfoSection.tsx';
import { IncomeStructureByKvedIncomesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/IncomeStructureByKvedIncomesSection.tsx';
import {
  applyIncomeStructureByKvedGeneralInfo,
  deserializeIncomeStructureByKvedCertificate,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/mapDtoToFormValues.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import { getIncomeStructureByKvedDefaultValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/utils.ts';
import type { TDocumentGenerationFormProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const IncomeStructureByKvedDocumentGenerationForm: React.FC<
  TDocumentGenerationFormProps
> = ({
  disabled,
  formId,
  onSaveIncomeStructureByKved,
  onLoadIncomeStructureByKved,
  onDirtyChange,
}) => {
  const hasLoadedCertificateRef = useRef(false);
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(
    Boolean(onLoadIncomeStructureByKved)
  );
  const form = useForm<TIncomeStructureByKvedFormValues>({
    defaultValues: getIncomeStructureByKvedDefaultValues(),
  });
  const { reset } = form;
  const isFormDisabled = disabled || isLoadingCertificate;

  useEffect(() => {
    if (form.formState.isDirty) {
      onDirtyChange?.();
    }
  }, [form.formState.isDirty, onDirtyChange]);

  useEffect(() => {
    if (!onLoadIncomeStructureByKved || hasLoadedCertificateRef.current) {
      return;
    }

    hasLoadedCertificateRef.current = true;

    void onLoadIncomeStructureByKved().then((response) => {
      if (!response) {
        setIsLoadingCertificate(false);
        return;
      }

      const values = response.body
        ? deserializeIncomeStructureByKvedCertificate(response.body)
        : getIncomeStructureByKvedDefaultValues();

      reset(applyIncomeStructureByKvedGeneralInfo(values, response));
      setIsLoadingCertificate(false);
    });
  }, [onLoadIncomeStructureByKved, reset]);

  const handleSubmit = async (values: TIncomeStructureByKvedFormValues) => {
    if (!onSaveIncomeStructureByKved) {
      return;
    }

    const isSaveSuccessful = await onSaveIncomeStructureByKved(values);

    if (isSaveSuccessful) {
      reset(values);
    }
  };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        id={formId}
        noValidate
        onSubmit={form.handleSubmit(handleSubmit)}
        sx={documentGenerationFormStyles.form}
      >
        <IncomeStructureByKvedGeneralInfoSection disabled={isFormDisabled} />
        <IncomeStructureByKvedIncomesSection disabled={isFormDisabled} />
      </Box>
    </FormProvider>
  );
};
